<template>
  <div class="module-usage-monthly-report">
    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="kpi-label">
          {{ $t('hit-admin.reports.moduleUsage.kpiUniqueUsers') }}
        </div>
        <div class="kpi-value">{{ kpis.total_unique_users }}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">
          {{ $t('hit-admin.reports.moduleUsage.kpiModulesUsed') }}
        </div>
        <div class="kpi-value">{{ kpis.modules_used_count }}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">
          {{ $t('hit-admin.reports.moduleUsage.kpiEvents') }}
        </div>
        <div class="kpi-value">{{ kpis.avg_active_days_per_user || 0 }}</div>
        <div class="kpi-sub">
          {{ $t('hit-admin.reports.moduleUsage.kpiAvgActiveDays') }}
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">
          {{ $t('hit-admin.reports.moduleUsage.kpiTopModule') }}
        </div>
        <div class="kpi-value small">{{ kpis.top_module || '-' }}</div>
        <div class="kpi-sub">
          {{ $t('hit-admin.reports.moduleUsage.kpiUsers') }}:
          {{ kpis.top_module_users || 0 }}
        </div>
        <div class="kpi-sub">
          {{ $t('hit-admin.reports.moduleUsage.kpiTrendUsers') }}:
          {{ formatPercent(kpis.trend_active_users_pct) }}
        </div>
      </div>
    </div>

    <div class="chart-card">
      <div class="chart-title">
        {{ $t('hit-admin.reports.moduleUsage.chartTitle') }}
      </div>
      <div class="chart-hint">
        Cliquer sur un module (barre ou ligne du tableau) pour afficher le
        détail utilisateurs.
      </div>
      <div v-if="chartRows.length === 0" class="chart-empty">
        {{ $t('hit-admin.reports.moduleUsage.noData') }}
      </div>
      <div v-else class="bars">
        <div
          v-for="row in chartRows"
          :key="row.log_type"
          class="bar-row"
          :class="{selected: selectedModuleType === row.log_type}"
          @click="selectModule(row.log_type)"
        >
          <div class="bar-label" :title="row.log_type">
            {{ formatModuleLabel(row.log_type) }}
            <span class="click-indicator">↗ détail</span>
          </div>
          <div class="bar-track">
            <div class="bar-fill" :style="{width: `${row.widthPct}%`}" />
          </div>
          <div class="bar-value">{{ row.nb_utilisateurs }}</div>
        </div>
      </div>
    </div>

    <div class="reports-grid-wrapper">
      <ag-grid-vue
        class="ag-theme-alpine reports-grid"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-model-type="'infinite'"
        :cache-block-size="pageSize"
        :max-blocks-in-cache="5"
        :infinite-initial-row-count="1"
        :row-height="40"
        :header-height="44"
        :animate-rows="false"
        :suppress-cell-focus="true"
        :suppress-field-dot-notation="true"
        :overlay-loading-template="loadingOverlay"
        :overlay-no-rows-template="noRowsOverlay"
        @grid-ready="onGridReady"
        @filter-changed="onFilterChanged"
        @cell-clicked="onCellClicked"
      />
    </div>

    <div v-if="selectedModuleType" class="chart-card">
      <div class="chart-title">
        {{ $t('hit-admin.reports.moduleUsage.drilldownTitle') }}:
        {{ formatModuleLabel(selectedModuleType) }}
      </div>
      <div v-if="drillRows.length === 0" class="chart-empty">
        {{ $t('hit-admin.reports.moduleUsage.noData') }}
      </div>
      <table v-else class="drill-table">
        <thead>
          <tr>
            <th>Utilisateur</th>
            <th>Société</th>
            <th>Jours actifs</th>
            <th>Dernière activité</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in drillRows" :key="r.user_id">
            <td>{{ r.username_email }}</td>
            <td>{{ r.company_designation }}</td>
            <td>{{ r.active_days }}</td>
            <td>{{ formatDateTime(r.last_seen) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {AgGridVue} from 'ag-grid-vue3';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'ModuleUsageMonthlyReport',
  components: {AgGridVue},
  emits: ['filters-changed', 'total-changed'],
  props: {
    filterText: {
      type: String,
      default: '',
    },
    periodMode: {
      type: String,
      default: 'month',
    },
    month: {
      type: String,
      default: '',
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    companyId: {
      type: String,
      default: null,
    },
    userId: {
      type: String,
      default: null,
    },
    moduleType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      gridApi: null,
      gridColumnApi: null,
      pageSize: 50,
      gridColumns: ['log_type', 'nb_utilisateurs', 'utilisateurs'],
      activeFilterCount: 0,
      activeFilterColumns: [],
      lastTotalRows: null,
      lastEmittedTotalRows: null,
      kpis: {
        total_unique_users: 0,
        modules_used_count: 0,
        total_events: 0,
        top_module: null,
        top_module_users: 0,
        avg_active_days_per_user: 0,
        trend_active_users_pct: 0,
      },
      chartRowsRaw: [],
      selectedModuleType: null,
      drillRows: [],
      loadingOverlay:
        '<div class="ag-overlay-loading-center">Chargement…</div>',
      noRowsOverlay:
        '<div class="ag-overlay-loading-center">Aucune donnée</div>',
    };
  },
  computed: {
    defaultColDef() {
      return {
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 120,
        filter: true,
        floatingFilter: false,
        suppressHeaderMenuButton: false,
        filterParams: {
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          debounceMs: 250,
        },
      };
    },
    columnDefs() {
      if (!this.gridColumns || this.gridColumns.length === 0) return [];
      return this.gridColumns.map((field) => ({
        field,
        headerName:
          field === 'log_type'
            ? 'Module'
            : field === 'nb_utilisateurs'
            ? 'Utilisateurs actifs'
            : field === 'adoption_pct'
            ? 'Adoption %'
            : field === 'trend_pct'
            ? 'Tendance %'
            : field,
        tooltipField: field,
        cellClass: 'hit-modern-cell',
        valueFormatter: (p) => {
          const v = p?.value;
          if (v === null || v === undefined) return '';
          if (field === 'log_type') return this.formatModuleLabel(String(v));
          if (field === 'adoption_pct' || field === 'trend_pct') {
            return this.formatPercent(v);
          }
          return String(v);
        },
      }));
    },
    chartRows() {
      const max = this.chartRowsRaw.reduce((acc, row) => {
        return Math.max(acc, Number(row.nb_utilisateurs || 0));
      }, 0);
      return this.chartRowsRaw.map((row) => {
        const count = Number(row.nb_utilisateurs || 0);
        const widthPct =
          max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 0;
        return {...row, widthPct};
      });
    },
  },
  methods: {
    getExportState() {
      const filterText = this.filterText || '';
      let filterModel = null;
      let sortModel = null;
      try {
        filterModel =
          this.gridApi && typeof this.gridApi.getFilterModel === 'function'
            ? this.gridApi.getFilterModel()
            : null;
      } catch {
        filterModel = null;
      }
      try {
        sortModel =
          this.gridApi && typeof this.gridApi.getSortModel === 'function'
            ? this.gridApi.getSortModel()
            : null;
      } catch {
        sortModel = null;
      }

      const primarySort = Array.isArray(sortModel) ? sortModel[0] : null;
      const sortBy = primarySort?.colId || null;
      const sortDir = primarySort?.sort || null;
      const activeColumns = Array.isArray(this.activeFilterColumns)
        ? this.activeFilterColumns
        : [];
      const hasColumnFilters = activeColumns.length > 0;
      const hasGlobalSearch = !!filterText && filterText.trim().length > 0;

      return {
        filterText,
        month: this.periodMode === 'month' ? this.month || null : null,
        startDate: this.periodMode === 'range' ? this.startDate || null : null,
        endDate: this.periodMode === 'range' ? this.endDate || null : null,
        companyId: this.companyId || null,
        userId: this.userId || null,
        moduleType: this.moduleType || null,
        filterModel,
        columnFiltersJson: filterModel ? JSON.stringify(filterModel) : null,
        sortModel,
        sortBy,
        sortDir,
        hasActiveFilters: hasColumnFilters || hasGlobalSearch,
        activeColumns,
        filteredTotal: this.lastTotalRows,
      };
    },
    formatModuleLabel(logType) {
      if (!logType) return '-';
      return String(logType)
        .replace(/^MODULE_USAGE_/, '')
        .replaceAll('_', ' ');
    },
    formatPercent(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '0%';
      return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
    },
    formatDateTime(v) {
      if (!v) return '-';
      return String(v)
        .replace('T', ' ')
        .replace(/(\.\d+)?Z?$/, '');
    },
    selectModule(moduleType) {
      this.selectedModuleType = moduleType || null;
      this.loadDrilldown();
    },
    async onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      await this.loadKpis();
      await this.loadChartData();
      this.reloadGrid();
    },
    refreshOnly() {
      this.loadKpis();
      this.loadChartData();
      this.reloadGrid();
    },
    resetAll() {
      if (!this.gridApi) return;
      if (typeof this.gridApi.setFilterModel === 'function') {
        this.gridApi.setFilterModel(null);
      }
      if (typeof this.gridApi.setSortModel === 'function') {
        this.gridApi.setSortModel([{colId: 'nb_utilisateurs', sort: 'desc'}]);
      }
      this.selectedModuleType = null;
      this.drillRows = [];
      this.reloadGrid();
      this.loadKpis();
      this.loadChartData();
    },
    reloadGrid() {
      if (!this.gridApi) return;
      this.loadKpis();
      this.loadChartData();
      if (this.selectedModuleType) this.loadDrilldown();
      const datasource = this.buildDatasource();
      if (typeof this.gridApi.setGridOption === 'function') {
        this.gridApi.setGridOption('datasource', datasource);
        if (typeof this.gridApi.refreshInfiniteCache === 'function') {
          this.gridApi.refreshInfiniteCache();
        }
      } else if (typeof this.gridApi.setDatasource === 'function') {
        this.gridApi.setDatasource(datasource);
      }
    },
    async loadKpis() {
      try {
        this.kpis = await adminApi.getModuleUsageMonthlyKpis({
          month: this.periodMode === 'month' ? this.month || null : null,
          startDate:
            this.periodMode === 'range' ? this.startDate || null : null,
          endDate: this.periodMode === 'range' ? this.endDate || null : null,
          companyId: this.companyId || null,
          userId: this.userId || null,
          moduleType: this.moduleType || null,
        });
      } catch {
        this.kpis = {
          total_unique_users: 0,
          modules_used_count: 0,
          total_events: 0,
          top_module: null,
          top_module_users: 0,
        };
      }
    },
    async loadChartData() {
      try {
        const res = await adminApi.getModuleUsageMonthly({
          page: 1,
          pageSize: 100,
          sortBy: 'nb_utilisateurs',
          sortDir: 'desc',
          filterText: this.filterText || null,
          columnFilters: null,
          month: this.periodMode === 'month' ? this.month || null : null,
          startDate:
            this.periodMode === 'range' ? this.startDate || null : null,
          endDate: this.periodMode === 'range' ? this.endDate || null : null,
          companyId: this.companyId || null,
          userId: this.userId || null,
          moduleType: this.moduleType || null,
        });
        this.chartRowsRaw = (res?.rows || []).slice(0, 12);
      } catch {
        this.chartRowsRaw = [];
      }
    },
    async loadDrilldown() {
      if (!this.selectedModuleType) {
        this.drillRows = [];
        return;
      }
      try {
        const res = await adminApi.getModuleUsageMonthlyDrilldown({
          moduleType: this.selectedModuleType,
          month: this.periodMode === 'month' ? this.month || null : null,
          startDate:
            this.periodMode === 'range' ? this.startDate || null : null,
          endDate: this.periodMode === 'range' ? this.endDate || null : null,
          companyId: this.companyId || null,
          userId: this.userId || null,
          page: 1,
          pageSize: 30,
        });
        this.drillRows = res?.rows || [];
      } catch {
        this.drillRows = [];
      }
    },
    buildDatasource() {
      const self = this;
      return {
        async getRows(params) {
          try {
            const startRow = params.startRow ?? 0;
            const endRow = params.endRow ?? self.pageSize;
            const pageSize = endRow - startRow;
            const page = Math.floor(startRow / pageSize) + 1;

            const sortModel = params.sortModel || [];
            const primarySort = sortModel[0] || null;
            const sortBy = primarySort?.colId || null;
            const sortDir = primarySort?.sort || null;

            const columnFilters = params.filterModel
              ? JSON.stringify(params.filterModel)
              : null;

            const res = await adminApi.getModuleUsageMonthly({
              page,
              pageSize,
              sortBy,
              sortDir,
              filterText: self.filterText || null,
              columnFilters,
              month: self.periodMode === 'month' ? self.month || null : null,
              startDate:
                self.periodMode === 'range' ? self.startDate || null : null,
              endDate:
                self.periodMode === 'range' ? self.endDate || null : null,
              companyId: self.companyId || null,
              userId: self.userId || null,
              moduleType: self.moduleType || null,
            });

            self.lastTotalRows = res?.total ?? null;
            if (
              typeof self.lastTotalRows === 'number' &&
              self.lastTotalRows !== self.lastEmittedTotalRows
            ) {
              self.lastEmittedTotalRows = self.lastTotalRows;
              self.$emit('total-changed', {total: self.lastTotalRows});
            }

            if (res?.columns?.length) {
              self.gridColumns = res.columns;
            }

            const rows = (res?.rows || []).map((r) => ({...r}));
            params.successCallback(rows, res?.total ?? rows.length);
          } catch {
            params.failCallback();
          }
        },
      };
    },
    onFilterChanged() {
      try {
        const model =
          this.gridApi && typeof this.gridApi.getFilterModel === 'function'
            ? this.gridApi.getFilterModel()
            : null;
        const cols =
          model && typeof model === 'object'
            ? Object.keys(model).filter((k) => model[k])
            : [];
        this.activeFilterColumns = cols;
        this.activeFilterCount = cols.length;
      } catch {
        this.activeFilterCount = 0;
        this.activeFilterColumns = [];
      }

      this.$emit('filters-changed', {
        count: this.activeFilterCount,
        columns: this.activeFilterColumns,
      });
      this.loadChartData();
      this.reloadGrid();
    },
    onCellClicked(event) {
      const moduleType =
        event?.colDef?.field === 'log_type'
          ? event?.value
          : event?.data?.log_type || null;
      if (moduleType) this.selectModule(moduleType);
    },
  },
});
</script>

<style lang="scss" scoped>
.module-usage-monthly-report {
  height: 100%;
  min-height: 0;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 0.75rem;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
}

.kpi-card {
  border: 1px solid var(--ho-border, rgba(15, 23, 42, 0.08));
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.7rem 0.85rem;
}

.kpi-label {
  font-size: 0.75rem;
  color: rgba(100, 116, 139, 1);
}

.kpi-value {
  margin-top: 0.3rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
}

.kpi-value.small {
  font-size: 0.95rem;
  font-weight: 650;
  word-break: break-word;
}

.kpi-sub {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: rgba(71, 85, 105, 1);
}

.chart-card {
  border: 1px solid var(--ho-border, rgba(15, 23, 42, 0.08));
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.7rem 0.85rem;
}

.chart-title {
  font-size: 0.82rem;
  font-weight: 650;
  color: rgba(15, 23, 42, 0.82);
  margin-bottom: 0.25rem;
}

.chart-hint {
  font-size: 0.74rem;
  color: rgba(71, 85, 105, 0.92);
  margin-bottom: 0.45rem;
}

.chart-empty {
  font-size: 0.82rem;
  color: rgba(100, 116, 139, 1);
}

.bars {
  display: grid;
  gap: 0.35rem;
}

.bar-row {
  display: grid;
  grid-template-columns: 16rem minmax(0, 1fr) 3rem;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  border-radius: 0.35rem;
  padding: 0.1rem 0.25rem;
  transition: background-color 120ms ease;
}

.bar-row:hover {
  background: rgba(15, 23, 42, 0.04);
}

.bar-row.selected {
  background: color-mix(in srgb, var(--color-accent, #00997c) 16%, #ffffff);
}

.bar-label {
  font-size: 0.75rem;
  color: rgba(15, 23, 42, 0.8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.click-indicator {
  margin-left: 0.4rem;
  font-size: 0.68rem;
  color: rgba(71, 85, 105, 0.9);
}

.bar-track {
  height: 0.7rem;
  background: rgba(148, 163, 184, 0.18);
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: color-mix(in srgb, var(--color-accent, #00997c) 88%, #0b3a33);
}

.bar-value {
  text-align: right;
  font-size: 0.75rem;
  color: rgba(15, 23, 42, 0.8);
}

.reports-grid-wrapper {
  min-height: 0;
  height: 100%;
}

.reports-grid {
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--ho-border, rgba(15, 23, 42, 0.08));
}

.reports-grid.ag-theme-alpine {
  --ag-foreground-color: rgba(15, 23, 42, 0.82);
  --ag-background-color: #ffffff;
  --ag-header-foreground-color: rgba(255, 255, 255, 0.96);
  --ag-header-background-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 96%,
    #0b3a33
  );
  --ag-odd-row-background-color: rgba(2, 6, 23, 0.02);
  --ag-row-hover-color: color-mix(
    in srgb,
    var(--color-accent, #00997c) 8%,
    #ffffff
  );
  --ag-border-color: var(--ho-border, rgba(15, 23, 42, 0.08));
}

.drill-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.drill-table th,
.drill-table td {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  padding: 0.35rem 0.4rem;
  text-align: left;
}

@media (max-width: 1280px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .bar-row {
    grid-template-columns: 10rem minmax(0, 1fr) 3rem;
  }
}
</style>
